"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaMapMarkerAlt } from "react-icons/fa";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useRegisterForm } from "../../hooks/useRegisterForm";
// Même helper que GoLiveStreamForm / SpecialtyFilter.
// Adaptez ce chemin selon votre arborescence.
import { getSpecialtyLabel } from "../../../Medicalcase/components/Specialtyfilter/specialtyLabel";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";

import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./RegisterForm2.css";

// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";
// layout d'origine de cette page : hero à gauche (classe signup-doctor)
const DEFAULT_HERO_ON_LEFT = true;

// Bornes du compteur d'expérience (années)
const EXPERIENCE_MIN = 0;
const EXPERIENCE_MAX = 60;

// Même liste que GoLiveStreamForm (valeurs anglaises envoyées à l'API).
export const DEFAULT_SPECIALTIES = [
  "Cardiology",
  "Neurology",
  "Medical Oncology",
  "Orthopedics",
  "Radiology",
  "Pediatrics",
  "Dermatology",
  "Psychiatry",
  "Gastroenterology",
  "Endocrinology",
  "Pulmonology",
  "Nephrology",
  "Rheumatology",
  "Urology",
  "Ophthalmology",
  "Otorhinolaryngology (ENT)",
  "Gynecology",
  "General Surgery",
  "Anesthesiology",
  "Emergency Medicine",
  "Critical Care / Intensive Care",
  "Intensive Medicine",
  "Anesthesiology and Intensive Care",
  "General",
];

export const buildSpecialtyOptions = (specialtyList = DEFAULT_SPECIALTIES, t) =>
  specialtyList.filter(Boolean).map((value) => ({
    value,
    label: getSpecialtyLabel(t, value),
  }));

/* ───────────────────────── Icônes ───────────────────────── */

const IChevron = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const IPlus = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IMinus = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/* ───────────────────────── Champs ───────────────────────── */

const FormInput = ({
  name,
  label,
  type = "text",
  placeholder = "",
  value,
  onChange,
  error,
  autoComplete,
  wrapperClassName = "",
}) => (
  <div className={`signup-form-group ${wrapperClassName}`.trim()}>
    {label && (
      <label className="signup-form-label" htmlFor={name}>
        {label}
      </label>
    )}
    <input
      id={name}
      name={name}
      type={type}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={onChange}
      className={`signup-form-input${error ? " signup-form-input-error" : ""}`}
      autoComplete={autoComplete}
    />
    {error ? (
      <span className="sp-field-error" role="alert">
        {error}
      </span>
    ) : null}
  </div>
);

// Liste déroulante personnalisée : identique à GoLiveStreamForm
// (hauteur réduite + scroll interne, sans recherche)
const FormCombo = ({
  name,
  label,
  value,
  onChange, // (value) => void
  error,
  options = [],
  placeholder = "Select...",
  wrapperClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const comboRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const found = options.find((opt) => opt.value === value);
    return found ? found.label : "";
  }, [options, value]);

  // Ferme la liste au clic en dehors ou à la touche Échap
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboRef.current && !comboRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div className={`signup-form-group ${wrapperClassName}`.trim()}>
      {label && (
        <label className="signup-form-label" htmlFor={name}>
          {label}
        </label>
      )}

      <div className={`rf2-combo${isOpen ? " is-open" : ""}`} ref={comboRef}>
        <button
          type="button"
          id={name}
          className={`signup-form-input rf2-combo-trigger${error ? " signup-form-input-error" : ""}`}
          onClick={() => setIsOpen((open) => !open)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className={selectedLabel ? "rf2-combo-value" : "rf2-combo-placeholder"}>
            {selectedLabel || placeholder}
          </span>
          <span className={`rf2-combo-chevron${isOpen ? " is-open" : ""}`}>
            <IChevron />
          </span>
        </button>

        {isOpen && (
          <ul className="rf2-combo-list" role="listbox" aria-label={label}>
            <li
              role="option"
              aria-selected={!value}
              className={`rf2-combo-option${!value ? " is-selected" : ""}`}
              onClick={() => handleSelect("")}
            >
              {placeholder}
            </li>
            {options.map((item) => (
              <li
                key={item.value}
                role="option"
                aria-selected={value === item.value}
                className={`rf2-combo-option${value === item.value ? " is-selected" : ""}`}
                onClick={() => handleSelect(item.value)}
              >
                {item.label}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <span className="sp-field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
};

// Compteur (− [nombre] +) pour les années d'expérience
const FormCounter = ({
  name,
  label,
  value,
  onChange, // (stringValue) => void
  error,
  min = EXPERIENCE_MIN,
  max = EXPERIENCE_MAX,
  unitLabel = "",
  decreaseLabel = "Decrease",
  increaseLabel = "Increase",
  wrapperClassName = "",
}) => {
  const current = Number(value) || 0;

  const clamp = (n) => Math.min(max, Math.max(min, n));

  const handleDecrease = () => onChange(String(clamp(current - 1)));
  const handleIncrease = () => onChange(String(clamp(current + 1)));

  // Saisie directe : uniquement des chiffres, valeur bornée
  const handleInput = (event) => {
    const raw = event.target.value.replace(/\D/g, "").slice(0, 2);
    if (raw === "") {
      onChange("");
      return;
    }
    onChange(String(clamp(Number(raw))));
  };

  // Si le champ est laissé vide, on remet la valeur minimale
  const handleBlur = () => {
    if (value === "" || value == null) onChange(String(min));
  };

  // Flèches haut/bas du clavier
  const handleKeyDown = (event) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      handleIncrease();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      handleDecrease();
    }
  };

  return (
    <div className={`signup-form-group ${wrapperClassName}`.trim()}>
      {label && (
        <label className="signup-form-label" htmlFor={name}>
          {label}
        </label>
      )}

      <div className={`rf2-counter${error ? " rf2-counter--error" : ""}`}>
        <button
          type="button"
          className="rf2-counter-btn"
          onClick={handleDecrease}
          disabled={current <= min}
          aria-label={decreaseLabel}
        >
          <IMinus />
        </button>

        <div className="rf2-counter-center">
          <input
            id={name}
            name={name}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="rf2-counter-input"
            value={value ?? ""}
            onChange={handleInput}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          {unitLabel ? <span className="rf2-counter-unit">{unitLabel}</span> : null}
        </div>

        <button
          type="button"
          className="rf2-counter-btn"
          onClick={handleIncrease}
          disabled={current >= max}
          aria-label={increaseLabel}
        >
          <IPlus />
        </button>
      </div>

      {error ? (
        <span className="sp-field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
};

/* ───────────────────────── Page ───────────────────────── */

export default function RegisterForm2() {
  const { form, errors, handleChange } = useRegisterForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? DEFAULT_HERO_ON_LEFT : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour le swap des 2 cards (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  // Le compteur démarre à 0 : on l'initialise directement dans le formulaire
  useEffect(() => {
    if (form.experience === "" || form.experience == null) {
      handleChange("experience", String(EXPERIENCE_MIN));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueAsPatient = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginPatient", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  const isPage2Valid = [
    "specialty",
    "experience",
    "practicePlace",
    "country",
    "city",
    "clinicAddress",
  ].every((f) => form[f] !== "" && form[f] != null && errors[f] === "");

  // Spécialités identiques à GoLiveStreamForm (valeur brute + libellé traduit)
  const specialtyOptions = useMemo(
    () => buildSpecialtyOptions(DEFAULT_SPECIALTIES, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  return (
    <div className="signup-container signup-doctor-auth">
      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form">
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("signup.doctor.step2Title", "Create Your")} {" "}
                <span className="signup-form-title-accent">
                  {t("signup.doctor.step2Accent", "Doctor Account")}
                </span>
              </h1>
            </div>

            <div className="signup-form-content">
              <div className="rf2-steps" aria-label="Progress indicator">
                <span className="rf2-step rf2-step--done" />
                <span className="rf2-step rf2-step--active" />
                <span className="rf2-step" />
                <span className="rf2-step" />
              </div>
              <p className="rf2-step-label">
                {t("signup.stepLabel2", "Step 2 / 4")}
              </p>
              <div className="rf2-section-description">
                <h2 className="rf2-description-title">
                  {t("signup.doctor.step2SectionTitle", "VOTRE PRATIQUE MÉDICALE")}
                </h2>
                <p className="rf2-description-text">
                  {t(
                    "signup.doctor.step2SectionDescription",
                    "Fournissez des informations professionnelles exactes pour que nous puissions vérifier votre profil et aider les patients à vous trouver."
                  )}
                </p>
              </div>

              <div className="signup-form-row">
                <FormCombo
                  name="specialty"
                  label={t("signup.doctor.specialtyLabel")}
                  value={form.specialty}
                  onChange={(val) => handleChange("specialty", val)}
                  options={specialtyOptions}
                  error={errors.specialty}
                  placeholder={t("signup.doctor.specialtyPlaceholder")}
                  wrapperClassName="signup-form-group--compact"
                />
                <FormCounter
                  name="experience"
                  label={t("signup.doctor.experienceLabel")}
                  value={form.experience}
                  onChange={(val) => handleChange("experience", val)}
                  error={errors.experience}
                  unitLabel={t("signup.doctor.experienceUnit", "years")}
                  decreaseLabel={t("signup.doctor.experienceDecrease", "Decrease")}
                  increaseLabel={t("signup.doctor.experienceIncrease", "Increase")}
                  wrapperClassName="signup-form-group--compact"
                />
              </div>

              <div className="signup-form-group">
                <label className="signup-form-label">
                  {t("signup.doctor.practicePlaceLabel")}
                </label>
                <div className="rf2-radioBtns">
                  <label className="rf2-radioOpt">
                    <input
                      type="radio"
                      value="Clinic/Hospital"
                      checked={form.practicePlace === "Clinic/Hospital"}
                      onChange={(e) => handleChange("practicePlace", e.target.value)}
                    />
                    <span className="rf2-radioTxt">
                      {t("signup.doctor.practicePlaceClinic")}
                    </span>
                  </label>
                  <label className="rf2-radioOpt">
                    <input
                      type="radio"
                      value="Private practice"
                      checked={form.practicePlace === "Private practice"}
                      onChange={(e) => handleChange("practicePlace", e.target.value)}
                    />
                    <span className="rf2-radioTxt">
                      {t("signup.doctor.practicePlacePrivate")}
                    </span>
                  </label>
                </div>
                {errors.practicePlace && (
                  <span className="sp-field-error" role="alert">
                    {errors.practicePlace}
                  </span>
                )}
              </div>

              <div className="signup-form-row">
                <FormInput
                  name="country"
                  label={t("signup.doctor.countryLabel")}
                  placeholder={t("signup.doctor.countryPlaceholder")}
                  value={form.country}
                  onChange={(e) => handleChange("country", e.target.value)}
                  error={errors.country}
                  autoComplete="country-name"
                  wrapperClassName="signup-form-group--compact"
                />
                <FormInput
                  name="city"
                  label={t("signup.doctor.cityLabel")}
                  placeholder={t("signup.doctor.cityPlaceholder")}
                  value={form.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  error={errors.city}
                  autoComplete="address-level2"
                  wrapperClassName="signup-form-group--compact"
                />
              </div>

              <div className="signup-form-group">
                <label className="signup-form-label" htmlFor="clinicAddress">
                  {t("signup.doctor.clinicAddressLabel")}
                </label>
                <div className="rf2-inputIcon">
                  <FaMapMarkerAlt aria-hidden />
                  <input
                    id="clinicAddress"
                    name="clinicAddress"
                    type="text"
                    placeholder={t("signup.doctor.clinicAddressPlaceholder")}
                    value={form.clinicAddress}
                    onChange={(e) => handleChange("clinicAddress", e.target.value)}
                    className={`signup-form-input${errors.clinicAddress ? " signup-form-input-error" : ""}`}
                    autoComplete="street-address"
                  />
                </div>
                {errors.clinicAddress && (
                  <span className="sp-field-error" role="alert">
                    {errors.clinicAddress}
                  </span>
                )}
              </div>

              <button
                className="signup-create-account-btn"
                disabled={!isPage2Valid}
                onClick={() => navigate("/register3")}
                type="button"
              >
                {t("commonn.continue", "Continue")}
              </button>

              <div className="signup-login-link-wrapper">
                <span className="signup-login-text">
                  {t("signup.alreadyHaveAccount", "Already have an account?")}{" "}
                  <span className="signup-login-link" onClick={() => navigate("/login")}>
                    {t("signup.signInNow", "Sign in now!")}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </section>

        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />
          <button
            type="button"
            className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
            onClick={() => navigate("/")}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>
          <div className="signup-hero-top-icon">
            <img src={icon1} alt="" />
          </div>
          <div className="signup-hero-inner">
            <h1 className="signup-hero-title">
              {t("signup.createAccountHeroTitle", "Are you signing up as a patient? This registration is for doctors only.")}
            </h1>
            <p className="signup-hero-sub">
              {t("signup.doctor.step2HeroText", "To book appointments and access care, please create a patient account instead.")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsPatient}
            >
              <span>{t("signup.continueAsPatient", "Continue as a patient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}